import classNames from 'classnames';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import styles from './SidebarRow.module.scss';
import addTestId from '../utils/addTestId';

interface SidebarRowRightContentProps {
  expanded?: boolean;
  onExpandClick?: React.MouseEventHandler;
  menuItems?: React.ReactNode;
  customMenu?: React.ReactNode;
}

export const SidebarRowRightContent = ({
  expanded,
  onExpandClick,
  menuItems,
  customMenu,
}: SidebarRowRightContentProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const clickHandledRef = useRef(false);
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const stopEvent = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    stopEvent(e);
    if (!clickHandledRef.current) {
      setIsMenuOpen(!isMenuOpen);
    }
    clickHandledRef.current = false;
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    if (onExpandClick) {
      stopEvent(e);
      onExpandClick(e);
    }
  };

  return (
    <div className={styles.rightContent}>
      {!selectedSectionId && (!!menuItems || !!customMenu) && (
        <div className={classNames(styles.menu, { [styles.open]: isMenuOpen })}>
          {customMenu}
          {!!menuItems && (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              className={styles.menuButton}
              onClick={handleMenuClick}
              {...addTestId('sidebarRowMenuButton')}
            >
              <OverflowMenu
                button={<SVGIcon size={16}>{overflowMenuIcon}</SVGIcon>}
                onOpenChange={(open) => {
                  setIsMenuOpen(open);
                  clickHandledRef.current = true;
                }}
                open={isMenuOpen}
                placement="bottom-start"
              >
                {menuItems}
              </OverflowMenu>
            </div>
          )}
        </div>
      )}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={classNames(styles.caretContainer, {
          [styles.hidden]: !onExpandClick,
        })}
        onClick={handleExpandClick}
      >
        <Caret direction={expanded ? 'up' : 'down'} size={10} />
      </div>
    </div>
  );
};

interface SidebarRowProps {
  icon?: React.ReactNode;
  rightContent?: React.ReactNode;
  subItem?: boolean;
  children: React.ReactNode;
  type?: 'post' | 'topic' | 'template';
  level?: number;
  to?: string;
  onClick?: React.MouseEventHandler;
  onlyActiveOnIndex?: boolean;
  active?: boolean;
}

const SidebarRow = ({
  icon,
  rightContent,
  subItem,
  children,
  type,
  level,
  to,
  onClick,
  onlyActiveOnIndex,
  active,
}: SidebarRowProps) => {
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  return (
    <div className={styles.rowContainer}>
      {!!level &&
        _.times(level, (index) => (
          <div
            key={index}
            className={classNames(styles.indentLine, {
              [styles.first]: index === 0,
              [styles.last]: index === level - 1,
            })}
          />
        ))}
      <Button
        to={to}
        onClick={(event) => {
          if (selectedSectionId) {
            event.preventDefault();
          }
          onClick?.(event);
        }}
        activeClassName={styles.active}
        onlyActiveOnIndex={onlyActiveOnIndex}
        className={classNames(styles.container, {
          [styles.subItem]: subItem,
          [styles.active]: active,
          [styles.post]: type === 'post',
          [styles.topic]: type === 'topic',
          [styles.template]: type === 'template',
        })}
      >
        {!!icon && (
          <div className={styles.icon}>
            {typeof icon === 'string' ? (
              <SVGIcon size={subItem ? 12 : 'regular'}>{icon}</SVGIcon>
            ) : (
              icon
            )}
          </div>
        )}
        <div className={styles.content}>{children}</div>
        {rightContent}
      </Button>
    </div>
  );
};

export default SidebarRow;
